import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, get, post } from "../services/apiClient";

export const NotificationChannel = {
  PUSH: "PUSH",
  SMS: "SMS",
  BOTH: "BOTH",
} as const;
export type NotificationChannel =
  (typeof NotificationChannel)[keyof typeof NotificationChannel];

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  isRead: boolean;
  sentAt: string;
}

interface NotificationListResponse {
  data: AppNotification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  unreadCount: number;
}

interface UnreadCountResponse {
  count: number;
}

export interface BroadcastPayload {
  audience: "ALL_CUSTOMERS" | "ALL_PROVIDERS" | "ALL_USERS";
  title: string;
  body: string;
}

const QUERY_KEY = ["notifications"];

/**
 * Polling cadence for notification queries. TanStack pauses these while the
 * window is unfocused, so an idle tab makes no requests; 2 minutes keeps a
 * focused tab responsive without pinning the API container awake.
 */
const POLL_INTERVAL_MS = 120_000;

const patch = (endpoint: string, body: unknown) =>
  apiClient(endpoint, { method: "PATCH", body });

export const useNotifications = (page = 1) =>
  useQuery<NotificationListResponse>({
    queryKey: [...QUERY_KEY, page],
    queryFn: () =>
      get<NotificationListResponse>(`/notifications?page=${page}&limit=20`),
    refetchInterval: POLL_INTERVAL_MS,
  });

export const useUnreadCount = () =>
  useQuery<UnreadCountResponse>({
    queryKey: [...QUERY_KEY, "unread-count"],
    queryFn: () => get<UnreadCountResponse>("/notifications/unread-count"),
    refetchInterval: POLL_INTERVAL_MS,
  });

export const useMarkRead = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (id) => patch(`/notifications/${id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useMarkAllRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => patch("/notifications/read-all", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export interface SendToUserPayload {
  userId: string;
  title: string;
  body: string;
}

export const useBroadcastNotification = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, BroadcastPayload>({
    mutationFn: (payload) => post("/notifications/broadcast", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useSendToUserNotification = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, SendToUserPayload>({
    mutationFn: (payload) => post("/notifications/send-to-user", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};
